<script setup lang="ts" generic="T extends string | number">
import NxRadioItem from './NxRadioItem.vue';
import NxSaveStateBadge from '../NxSaveStateBadge.vue';
import { NxRadioItemBaseProps } from './radio-internal.js';

const props = defineProps<
  NxRadioItemBaseProps<T> & {
    /** Defaults to 's' */
    height?: 'xs' | 's' | 'm' | 'l' | 'xl';
    variant?: 'filled' | 'light';
    iconOnly?: boolean;
  }
>();
</script>

<template>
  <NxRadioItem v-bind="props">
    <template #default="{ checked, disabled, saveState, errorMessage, saveFadeOut }">
      <div
        class="nx-radio-card-item"
        :class="[
          `variant-${props.variant ?? 'filled'} height-${props.height ?? 's'}`,
          { checked, 'icon-only': props.iconOnly, disabled },
        ]"
        :data-save-status="saveState"
      >
        <slot />
      </div>
      <!-- Sibling of the visual (anchored to .nx-radio) so the disabled fade
           of the card never dims the badge. -->
      <NxSaveStateBadge
        v-if="checked"
        class="save-badge"
        :state="saveState"
        :error-message="errorMessage"
        :fade-out="saveFadeOut"
      />
    </template>
  </NxRadioItem>
</template>

<style scoped>
.nx-radio-card-item {
  font-size: var(--nxt-font-size-sm);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--nxt-space-xs);
  cursor: pointer;

  border-radius: var(--nxt-radius-md);
  border: var(--nxt-hairline) solid var(--nxt-border);
  padding: var(--nxt-space-xs);
  /* one control line, so a card sits level with buttons and fields */
  line-height: var(--nxt-line-height-control);

  &.icon-only {
    /* same height as "normal" text: line-height + 2 * padding-block */
    height: calc(1lh + 2 * var(--nxt-space-xs));
    /* width = height */
    width: calc(1lh + 2 * var(--nxt-space-xs));
    position: relative;

    & > * {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
}

.nx-radio-card-item.variant-filled {
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-brand-bg);
  background-color: transparent;
  border-color: var(--nxt-border);

  &:hover:not(.disabled) {
    background-color: var(--nxt-brand-subtle-bg);
    border-color: var(--nxt-border-strong);

    &.checked {
      color: var(--nxt-brand-fg);
      background-color: var(--nxt-brand-bg-hover);
      border-color: var(--nxt-brand-bg);
    }
  }
  &.checked {
    color: var(--nxt-brand-fg);
    background-color: var(--nxt-brand-bg);
    border-color: var(--nxt-brand-bg);
  }
}

.nx-radio-card-item.variant-light {
  color: var(--nxt-brand-bg);
  background-color: transparent;
  border-color: var(--nxt-border);

  &:hover:not(.disabled) {
    background-color: var(--nxt-brand-subtle-bg);
    border-color: var(--nxt-border-strong);

    &.checked {
      background-color: var(--nxt-brand-subtle-bg);
      border-color: var(--nxt-border-strong);
    }
  }
  &.checked {
    background-color: var(--nxt-brand-subtle-bg);
    border-color: var(--nxt-brand-bg);
  }
}

/* Disabled (incl. group save-loading): recolor instead of fading. The badge is
   a SIBLING of the card (anchored to .nx-radio), so it stays fully visible.
   The disabled/loading cursor is handled centrally in NxRadioItem. */
.nx-radio-card-item.disabled {
  background-color: var(--nxt-disabled-bg);
  border-color: var(--nxt-border);
  color: var(--nxt-disabled-fg);
}

/* Checked + disabled needs its own (equal-specificity, later-wins) override:
   the variant blocks above set checked colors via `.variant-*.checked`
   (3 classes) which otherwise outranks the 2-class `.disabled` rule. */
.nx-radio-card-item.checked.disabled {
  background-color: var(--nxt-disabled-bg);
  border-color: var(--nxt-border);
  color: var(--nxt-disabled-fg);
}

/* --- I-422 save status: corner badge on the SELECTED card only; the card's own
   colors stay untouched. */
.save-badge {
  position: absolute;
  top: calc(-1 * var(--nxt-space-md) * 0.438);
  right: calc(-1 * var(--nxt-space-md) * 0.438);
}

.nx-radio-group[data-dense-radio-group='true'] .nx-radio-card-item {
  border-radius: 0;
  &.checked {
    z-index: var(--nxt-z-raised);
    position: relative;
  }
}
.nx-radio-group[data-dense-radio-group='true'] .nx-radio-label + .nx-radio .nx-radio-card-item,
.nx-radio-group[data-dense-radio-group='true'] .nx-radio + .nx-radio .nx-radio-card-item {
  margin-inline-start: calc(
    var(--nxt-size-unit) * -0.0625
  ); /* stylelint-disable-line declaration-property-unit-disallowed-list -- 1px optical nudge */
}
.nx-radio-group[data-dense-radio-group='true'] .nx-radio:first-child .nx-radio-card-item {
  border-start-start-radius: var(--nxt-radius-md);
  border-end-start-radius: var(--nxt-radius-md);
}
/* `:last-of-type`, not `:last-child` — the group renders its sr-only status
   span (I-422) as the last child, so the last .nx-radio <label> is never
   :last-child. The sr-only node is a <span>, items are <label>s. */
.nx-radio-group[data-dense-radio-group='true'] .nx-radio:last-of-type .nx-radio-card-item {
  border-start-end-radius: var(--nxt-radius-md);
  border-end-end-radius: var(--nxt-radius-md);
}

.nx-radio-group.layout-grid .nx-radio,
.nx-radio-group.layout-grid .nx-radio-card-item {
  width: 100%;
}

.height-xs {
  font-size: var(--nxt-font-size-xs);
}
.height-s {
  font-size: var(--nxt-font-size-sm);
}
.height-m {
  font-size: var(--nxt-font-size-md);
}
.height-l {
  font-size: var(--nxt-font-size-lg);
}
.height-xl {
  font-size: var(--nxt-font-size-xl);
}
</style>
